<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisTabs from 'dashboard/components-next/LaisTabs.vue';
import SalesDetailCommission from './SalesDetailCommission.vue';
import SalesDetailProperty from './SalesDetailProperty.vue';
import SalesDeliverySummaryParties from './SalesDeliverySummaryParties.vue';
import SalesDeliverySummaryRank from './SalesDeliverySummaryRank.vue';

const props = defineProps({
  loading: { type: Boolean, default: false },
  transaction: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['back', 'edit']);
const { t } = useI18n();
const activeTab = ref('rank');
const editOpen = ref(false);

const tabs = computed(() =>
  [
    {
      id: 'rank',
      label: t('SALES.DELIVERY.SUMMARY.TABS.RANK'),
      icon: 'i-lucide-rocket',
    },
    {
      id: 'property',
      label: t('SALES.DELIVERY.SUMMARY.TABS.PROPERTY'),
      icon: 'i-lucide-house',
    },
    {
      id: 'sellers',
      label: t('SALES.DELIVERY.SUMMARY.TABS.SELLERS'),
      icon: 'i-lucide-user-round',
    },
    {
      id: 'buyers',
      label: t('SALES.DELIVERY.SUMMARY.TABS.BUYERS'),
      icon: 'i-lucide-users-round',
    },
    {
      id: 'commission',
      label: t('SALES.DELIVERY.SUMMARY.TABS.COMMISSION'),
      icon: 'i-lucide-circle-dollar-sign',
    },
  ].map(tab => ({
    ...tab,
    disabled: props.loading && tab.id !== 'rank',
  }))
);
const editItems = computed(() => [
  {
    id: 'imovel',
    label: t('SALES.DELIVERY.SUMMARY.EDIT.PROPERTY'),
    icon: 'i-lucide-house',
  },
  {
    id: 'vendedores',
    label: t('SALES.DELIVERY.SUMMARY.EDIT.SELLERS'),
    icon: 'i-lucide-user-round',
  },
  {
    id: 'compradores',
    label: t('SALES.DELIVERY.SUMMARY.EDIT.BUYERS'),
    icon: 'i-lucide-users-round',
  },
  {
    id: 'comissao',
    label: t('SALES.DELIVERY.SUMMARY.EDIT.COMMISSION'),
    icon: 'i-lucide-circle-dollar-sign',
  },
]);
const canEdit = computed(
  () =>
    !props.loading &&
    props.transaction.permissions?.can_save_draft &&
    !props.transaction.receipt?.integration
);
const subtitle = computed(() =>
  [props.transaction.title, props.transaction.address?.endereco]
    .filter(Boolean)
    .join(t('SALES.COMMON.SEPARATOR'))
);
const selectEdit = section => {
  editOpen.value = false;
  emit('edit', section);
};

watch(
  () => props.loading,
  loading => {
    if (loading) activeTab.value = 'rank';
  }
);
</script>

<template>
  <div data-testid="sales-delivery-summary">
    <LaisHeader
      :title="t('SALES.DELIVERY.SUMMARY.HEADER_TITLE')"
      :subtitle="subtitle || t('SALES.DELIVERY.SUMMARY.HEADER_SUBTITLE')"
      :breadcrumb="[
        { label: t('SALES.TITLE') },
        { label: t('SALES.DELIVERY.SUMMARY.BREADCRUMB') },
      ]"
      show-back
      :back-label="t('SALES.ACTIONS.BACK')"
      @back="emit('back')"
    >
      <template v-if="canEdit" #actions>
        <div class="relative">
          <LaisPrimaryButton
            data-testid="sales-delivery-summary-edit-trigger"
            variant="ghost"
            icon="i-lucide-pencil"
            @click="editOpen = !editOpen"
          >
            {{ t('SALES.DELIVERY.SUMMARY.EDIT.TITLE') }}
          </LaisPrimaryButton>
          <div
            v-if="editOpen"
            class="absolute right-0 z-20 mt-2 w-56 rounded-lais border border-lais-border bg-white p-2 shadow-lais-pop"
          >
            <p
              class="px-3 py-2 text-xs font-bold uppercase text-lais-text-muted"
            >
              {{ t('SALES.DELIVERY.SUMMARY.EDIT.MENU_TITLE') }}
            </p>
            <button
              v-for="item in editItems"
              :key="item.id"
              :data-testid="`sales-delivery-summary-edit-${item.id}`"
              type="button"
              class="flex w-full items-center gap-2 rounded-lais-sm px-3 py-2 text-left text-sm text-lais-text-body hover:bg-lais-primary-50"
              @click="selectEdit(item.id)"
            >
              <span class="size-4" :class="item.icon" aria-hidden="true" />
              {{ item.label }}
            </button>
          </div>
        </div>
      </template>
    </LaisHeader>

    <main class="mx-auto w-full max-w-[1280px] px-4 py-6 sm:px-6 lg:px-10">
      <LaisTabs
        v-model="activeTab"
        :tabs="tabs"
        :aria-label="t('SALES.DELIVERY.SUMMARY.TABS_ARIA')"
      >
        <template #rank>
          <SalesDeliverySummaryRank
            :loading="loading"
            :summary="transaction.delivery_summary"
          />
        </template>
        <template #property>
          <SalesDetailProperty :transaction="transaction" summary />
        </template>
        <template #sellers>
          <SalesDeliverySummaryParties
            :transaction="transaction"
            role="vendedor"
          />
        </template>
        <template #buyers>
          <SalesDeliverySummaryParties
            :transaction="transaction"
            role="comprador"
          />
        </template>
        <template #commission>
          <SalesDetailCommission :transaction="transaction" summary />
        </template>
      </LaisTabs>
    </main>
  </div>
</template>
